// Adapted from https://beui.dev/r/image-viewer; MIT notice in components/beui/LICENSE.txt.
"use client";
// beui.dev/components/motion/image-viewer

import { ChevronLeft, ChevronRight, Minus, Plus, X } from "lucide-react";
import {
  type HTMLMotionProps,
  type Variants,
  AnimatePresence,
  LayoutGroup,
  motion,
  useMotionValue,
  useIsPresent,
  useReducedMotion,
} from "motion/react";
import {
  type ComponentPropsWithRef,
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  EASE_DRAWER,
  EASE_OUT,
  SPRING_LAYOUT,
  SPRING_PRESS,
} from "../../lib/beui/ease";
import { useModalScope } from "../../lib/beui/useModalScope";
import { PresenceGate } from "../../lib/beui/PresenceGate";
import { cn } from "../../lib/beui/utils";


import type { LightboxImage } from "./ImageViewer";
type ImageSwap = { direction: -1 | 1; reduce: boolean };
// Paging needs a definite arrival; an overdamped panel spring creeps through
// the last few pixels before snapping to its rest threshold.
const IMAGE_SLIDE_TRANSITION = {
  type: "tween",
  duration: 0.28,
  ease: EASE_DRAWER,
} as const;
// Use Motion's x channel so the final slide position is painted in the same
// frame as its animation. A native transform animation can briefly expose its
// initial inline transform when it releases ownership at completion.
const imageSwapVariants: Variants = {
  enter: ({ direction, reduce }: ImageSwap) => ({
    opacity: reduce ? 0 : 1,
    x: `${reduce ? 0 : direction * 100}%`,
  }),
  visible: ({ reduce }: ImageSwap) => ({
    opacity: 1,
    x: "0%",
    transition: reduce
      ? { duration: 0.1, ease: EASE_OUT }
      : IMAGE_SLIDE_TRANSITION,
  }),
  exit: ({ direction, reduce }: ImageSwap) => ({
    opacity: reduce ? 0 : 1,
    x: `${reduce ? 0 : direction * -100}%`,
    transition: reduce
      ? { duration: 0.1, ease: EASE_OUT }
      : IMAGE_SLIDE_TRANSITION,
  }),
};

export const controlClass =
  "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-30";

export function LightboxFrame({
  image,
  index,
  layoutId,
  reduce,
  onSwipe,
}: {
  image: LightboxImage;
  index: number;
  layoutId?: string;
  reduce: boolean;
  onSwipe: (direction: -1 | 1) => void;
}) {
  const [zoom, setZoom] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [dimensions, setDimensions] = useState<{ session: string; width: number; height: number } | null>(null);
  const session = `${image.id}-${image.src}`;
  // Reserve the listing crop ratio until the selected photo reveals its historical dimensions.
  const width = image.width ?? (dimensions?.session === session ? dimensions.width : 1200);
  const height = image.height ?? (dimensions?.session === session ? dimensions.height : 900);
  const [selection, setSelection] = useState<{
    session: string;
    index: number;
    direction: -1 | 1;
  }>({ session, index, direction: 1 });
  const changed = selection.session !== session;
  const direction = changed
    ? index < selection.index
      ? -1
      : 1
    : selection.direction;
  // Keep the shared-layout frame mounted; only the image layer changes identity.
  if (changed) {
    setSelection({ session, index, direction });
    setZoom(false);
    setFailed(false);
    setAttempt(0);
  }
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const present = useIsPresent();
  const bounds = useRef<HTMLDivElement>(null);
  const imageBox = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panHelp = useId();
  const positionSession = useRef(session);
  useLayoutEffect(() => {
    if (positionSession.current === session) return;
    positionSession.current = session;
    x.set(0);
    y.set(0);
    swipe.current = null;
  }, [session, x, y]);
  useLayoutEffect(() => {
    const measure = () => {
      if (!bounds.current || !imageBox.current) return;
      const next = {
        x: Math.max(
          0,
          (imageBox.current.clientWidth * 2 - bounds.current.clientWidth) / 2,
        ),
        y: Math.max(
          0,
          (imageBox.current.clientHeight * 2 - bounds.current.clientHeight) / 2,
        ),
      };
      setPan(next);
      x.set(Math.max(-next.x, Math.min(next.x, x.get())));
      y.set(Math.max(-next.y, Math.min(next.y, y.get())));
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (bounds.current) observer.observe(bounds.current);
    if (imageBox.current) observer.observe(imageBox.current);
    return () => observer.disconnect();
  }, [x, y]);
  return (
    <div className="relative flex min-h-0 flex-1 items-center justify-center">
      <div
        ref={bounds}
        className="flex h-full w-full items-center justify-center overflow-hidden rounded-lg"
      >
        <motion.div
          ref={imageBox}
          data-lightbox-content=""
          layoutId={layoutId}
          transition={SPRING_LAYOUT}
          className="relative flex max-h-full max-w-full items-center justify-center"
          style={{
            aspectRatio: `${width} / ${height}`,
            width: `min(100%, calc((100dvh - 160px) * ${width / height}))`,
          }}
        >
          <div className={cn("absolute inset-0", !zoom && "overflow-hidden")}>
            <AnimatePresence initial={false} custom={{ direction, reduce }}>
              <PresenceGate key={session}>
                {({ isPresent: slidePresent, gate }) => (
                  <motion.div
                    {...gate}
                    custom={{ direction, reduce }}
                    variants={imageSwapVariants}
                    initial="enter"
                    animate="visible"
                    exit="exit"
                    className="absolute inset-0"
                  >
                    {/* biome-ignore lint/performance/noImgElement: Copy-paste registry images must work outside Next.js. */}
                    <motion.img
                      key={attempt}
                      src={image.src}
                      alt={image.alt}
                      width={image.width}
                      height={image.height}
                      draggable={false}
                      onLoad={(event) => {
                        if (slidePresent && present) {
                          const picture = event.currentTarget;
                          setDimensions({ session, width: picture.naturalWidth, height: picture.naturalHeight });
                        }
                      }}
                      onError={() => {
                        if (slidePresent && present) setFailed(true);
                      }}
                      // Keep Motion's drag/layout registration mounted across zoom changes.
                      drag
                      dragListener={zoom}
                      dragConstraints={{
                        left: -pan.x,
                        right: pan.x,
                        top: -pan.y,
                        bottom: pan.y,
                      }}
                      dragElastic={0.08}
                      dragMomentum={false}
                      animate={{
                        scale: zoom && present ? 2 : 1,
                        ...(!present ? { x: 0, y: 0 } : {}),
                      }}
                      transition={reduce ? { duration: 0 } : SPRING_LAYOUT}
                      onPointerDown={(event) => {
                        // Swiping the image keeps keyboard focus on the controls.
                        event.preventDefault();
                        if (!zoom && event.isPrimary)
                          swipe.current = {
                            x: event.clientX,
                            y: event.clientY,
                          };
                      }}
                      onPointerUp={(event) => {
                        const start = swipe.current;
                        swipe.current = null;
                        if (!start || zoom) return;
                        const dx = event.clientX - start.x;
                        const dy = event.clientY - start.y;
                        if (
                          Math.abs(dx) > 50 &&
                          Math.abs(dx) > Math.abs(dy) * 1.5
                        )
                          onSwipe(dx < 0 ? 1 : -1);
                      }}
                      onPointerCancel={() => {
                        swipe.current = null;
                      }}
                      className="block h-full w-full select-none object-contain"
                      style={{
                        x,
                        y,
                        touchAction: zoom ? "none" : "pan-y",
                        cursor: zoom ? "grab" : undefined,
                      }}
                    />
                  </motion.div>
                )}
              </PresenceGate>
            </AnimatePresence>
          </div>
          {!failed && image.width === undefined && dimensions?.session !== session && <p role="status" className="pointer-events-none absolute text-sm text-white">Loading photo…</p>}
          {failed && (
            <div
              role="status"
              className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-neutral-900 p-4 text-center text-sm text-white"
            >
              <p>Unable to load this image.</p>
              <button type="button" className="min-h-11 rounded border border-white/40 px-4" onClick={(event) => {
                event.currentTarget.closest('[role="dialog"]')?.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
                setFailed(false); setAttempt(current => current + 1);
              }}>Retry</button>
            </div>
          )}
        </motion.div>
      </div>
      <button
        type="button"
        aria-label={zoom ? "Zoom out" : "Zoom in"}
        aria-pressed={zoom}
        aria-describedby={panHelp}
        onKeyDown={(event) => {
          if (!zoom) return;
          const dx =
            event.key === "ArrowLeft"
              ? -40
              : event.key === "ArrowRight"
                ? 40
                : 0;
          const dy =
            event.key === "ArrowUp" ? -40 : event.key === "ArrowDown" ? 40 : 0;
          if (!dx && !dy) return;
          event.preventDefault();
          event.stopPropagation();
          x.set(Math.max(-pan.x, Math.min(pan.x, x.get() + dx)));
          y.set(Math.max(-pan.y, Math.min(pan.y, y.get() + dy)));
        }}
        onClick={() => {
          x.set(0);
          y.set(0);
          setZoom((current) => !current);
        }}
        className={cn(controlClass, "absolute bottom-3 right-3 bg-black/60")}
      >
        {zoom ? (
          <Minus size={18} aria-hidden="true" />
        ) : (
          <Plus size={18} aria-hidden="true" />
        )}
      </button>
      <span id={panHelp} className="sr-only">
        When zoomed in, drag the image to pan, or keep this control focused and
        use the arrow keys.
      </span>
    </div>
  );
}
